import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserDto } from '@api/generated/bff';
import { useUser } from '@hooks/useUser.ts';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { bootSmartsupp, SmartsuppVariables, SmartsuppVisitor } from '@utils/smartsupp.ts';

/** `phone` se do BFF UserDto přidává — dokud není klient přegenerovaný, typ ho ještě nezná. */
type UserWithPhone = UserDto & { phone?: string };

const fullName = (user: UserDto) => [user.firstName, user.familyName].filter(Boolean).join(' ') || user.email;

/**
 * Co má operátor podpory vidět v detailu návštěvníka, aby nemusel nic dohledávat.
 * Klíče slouží i jako popisky v dashboardu, proto bez diakritiky a mezer.
 */
const buildVariables = (user: UserDto, orgId: number, portalId: number): SmartsuppVariables => {
  const variables: SmartsuppVariables = {
    ID_uzivatele: user.id,
  };

  const organizations = user.organizations ?? [];
  const org = organizations.find(o => o.id === orgId) ?? organizations.firstOrNull();
  if (org) {
    variables.Organizace = org.ic ? `${org.name} (IC ${org.ic})` : org.name;
    if (org.shortname) variables.Zkratka = org.shortname;
  }

  const portal = org?.portals.find(p => p.id === portalId) ?? org?.portals.firstOrNull();
  if (portal) {
    variables.Portal = `${portal.name} (${portal.shortcut})`;
    variables.Role = portal.isSupplier ? 'dodavatel' : 'klient';
  }

  if (user.impersonatedBy) {
    variables.Impersonace = `prihlasen admin ID ${user.impersonatedBy}`;
  }

  return variables;
};

const toVisitor = (user: UserWithPhone, orgId: number, portalId: number, language: string): SmartsuppVisitor => ({
  name: fullName(user),
  email: user.email,
  phone: user.phone,
  language,
  variables: buildVariables(user, orgId, portalId),
});

/**
 * Nastartuje Smartsupp ve chvíli, kdy je známý přihlášený uživatel, a drží v chatu jeho
 * aktuální identitu (přelogování, impersonace, přepnutí organizace/portálu i jazyka).
 */
export const useSmartsupp = () => {
  const beUser = useUser(s => s.beUser) as UserWithPhone;
  const orgId = useOrgPortal(s => s.orgId);
  const portalId = useOrgPortal(s => s.portalId);
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!beUser?.id) return;
    bootSmartsupp(toVisitor(beUser, orgId, portalId, i18n.language));
  }, [beUser, orgId, portalId, i18n.language]);
};
